import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, seedAPI } from '../lib/api';
import api from '../lib/api';

const AuthContext = createContext(null);

// Définition des permissions par module (fallback si API indisponible)
// Modules granulaires du menu (moduleId) → permission large contrôlée par les routes/API.
// Garder synchronisé avec navSections (components/Layout.jsx).
const PERM_MODULE_EQUIV = {
  ai_intelligence: ['knowledge'],
  audit_hygiene: ['tracking_labels'],
  boufi_network: ['corner_copilot'],
  boutique_copilot: ['corner_copilot'],
  corner_staff: ['corner_copilot'],
  corner_view: ['corner_copilot'],
  dashboard: ['cockpit', 'kpis_pilotage', 'performance'],
  fidelite_admin: ['cuisine', 'formation_fidelite', 'moments_admin', 'wa_shop', 'whatsapp_inbox', 'whatsapp_manager', 'whatsapp_templates'],
  formations: ['bouffi_assistant'],
  planning: ['fidelite', 'messagerie_interne'],
  production_plan: ['comptage_cookies', 'stock_vitrine'],
  predictions: ['calendrier'],
  rentabilite: ['alert_cockpit', 'audit_marges', 'biborne_database', 'ca_corrections', 'cerveau_boufi', 'codes_promo', 'content_studio', 'creators_admin', 'cross_analysis', 'crust', 'dashboard_biborne', 'dashboard_global', 'drop_engine', 'fiches_de_paie', 'food_cost', 'gestion_couts', 'historique_ventes', 'horaires', 'hub_equipe', 'journal_direction', 'launches', 'menu_import', 'mercuriale', 'multicanal', 'produits_db', 'rapport_campagnes', 'rapport_hebdo', 'referentiel_clients', 'referentiel_produits', 'simulateur', 'staffing_optimizer', 'uber_eats_import', 'ubereats'],
  users_manage: ['schedulers', 'whatsapp_brief'],
};


const DEFAULT_PERMISSIONS = {
  dashboard: ["admin", "inspecteur", "licencie"],
  dashboard_global: ["admin"],
  carte: ["admin", "inspecteur"],
  boutiques: ["admin", "inspecteur", "licencie", "manager"],
  inspections: ["admin", "inspecteur", "licencie"],
  audit_hygiene: ["admin", "inspecteur", "licencie", "corner_manager", "corner_ops"],
  reseau: ["admin", "corner_manager", "corner_ops"],
  pilotage_alize: ["admin", "corner_ops"],
  inspections_create: ["admin", "inspecteur"],
  rentabilite: ["admin", "inspecteur", "licencie"],
  rentabilite_reseau: ["admin"],
  comptage_cookies: ["admin", "manager"],
  galerie_cameras: ["admin"],
  stock_frigo: ["admin", "manager"],
  stock_vitrine: ["admin", "manager"],
  production_plan: ["admin", "manager", "corner_ops"],
  ai_intelligence: ["admin", "inspecteur", "licencie"],
  reputation: ["admin", "inspecteur", "licencie", "manager"],
  formations: ["admin", "inspecteur", "licencie", "manager"],
  formations_manage: ["admin"],
  alertes: ["admin"],
  users_manage: ["admin"],
  innovations: ["admin", "inspecteur", "licencie"],
  comparatif: ["admin"],
  guide: ["admin", "inspecteur", "licencie"],
  boufi_league: ["admin", "manager"],
  predictions: ["admin", "inspecteur", "licencie", "manager"],
  planning: ["admin", "manager"],
  fidelite_admin: ["admin", "manager"],
  influence_marketing: ["admin"],
  editions_limitees: ["admin"],
  rapports: ["admin", "manager"],
  messagerie_interne: ["admin", "manager"],
  performance: ["admin", "inspecteur", "licencie"],
  multicanal: ["admin", "inspecteur", "licencie"],
  scalabilite: ["admin"],
  kpis_pilotage: ["admin", "inspecteur", "licencie"],
  whatsapp_brief: ["admin"],
  schedulers: ["admin"],
  codes_promo: ["admin", "manager", "inspecteur"],
  settings: ["admin"],
  corner_view: ["admin", "corner_ops", "corner_manager", "corner_referent"],
  boufi_network: ["admin", "corner_ops"],
  corner_staff: ["admin", "corner_ops", "corner_manager"],
  corner_settings: ["admin"],
  boutique_copilot: ["admin", "corner_ops", "manager"],
};

// Labels des rôles
const ROLE_LABELS = {
  admin: "Direction",
  inspecteur: "Inspecteur",
  licencie: "Licencié",
  manager: "Manager",
  associe: "Associé",
  corner_ops: "Direction Opérationnelle",
  corner_manager: "Responsable Corners",
  corner_referent: "Référent Corner"
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [modulePermissions, setModulePermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBoutiqueId, setSelectedBoutiqueIdState] = useState(() => localStorage.getItem('boufi_boutique_id') || null);
  const setSelectedBoutiqueId = useCallback((id) => {
    setSelectedBoutiqueIdState(id);
    if (id) localStorage.setItem('boufi_boutique_id', id);
  }, []);

  // Vérifier si l'utilisateur a une permission
  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    // Galerie Caméras : réservée à la Direction (admin) et à Rania uniquement
    if (permission === 'galerie_cameras') {
      const ident = `${user.name || ''} ${user.email || ''}`.toLowerCase();
      if (user.role === 'admin' || ident.includes('rania')) return true;
      return Array.isArray(user.custom_permissions) && user.custom_permissions.includes('galerie_cameras');
    }
    const perms = modulePermissions || DEFAULT_PERMISSIONS;
    // Si l'utilisateur a des permissions custom (liste NON vide), elles sont la source unique de vérité
    // (environnement sur mesure, ex. Alizé) — une liste vide est ignorée (fallback rôle).
    if (Array.isArray(user.custom_permissions) && user.custom_permissions.length > 0) {
      if (user.custom_permissions.includes(permission)) return true;
      // Un module granulaire du menu accorde la permission large correspondante
      return (PERM_MODULE_EQUIV[permission] || []).some((m) => user.custom_permissions.includes(m));
    }
    // Les accès Corner/Boutique Copilot suivent le rôle (équipe corners)
    if ((permission.startsWith('corner_') || permission === 'boutique_copilot') && perms[permission]?.includes(user.role)) {
      return true;
    }
    return perms[permission]?.includes(user.role) || false;
  }, [user, modulePermissions]);

  // Lecture seule (associé) ?
  const isReadOnly = useCallback(() => {
    return Boolean(user?.read_only);
  }, [user]);

  // Vérifier si l'utilisateur peut accéder à une boutique
  const canAccessBoutique = useCallback((boutiqueId) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'inspecteur') return true;
    return user.boutiques_ids?.includes(boutiqueId) || false;
  }, [user]);

  // Vérifier si l'utilisateur a accès à toutes les boutiques
  const hasAllBoutiques = useCallback(() => {
    if (!user) return false;
    return user.role === 'admin' || user.role === 'inspecteur';
  }, [user]);

  // Charger les permissions depuis l'API
  const loadPermissions = useCallback(async () => {
    try {
      const res = await api.get('/auth/permissions');
      setPermissions(res.data.permissions || {});
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  }, []);

  // Charger les permissions modules depuis les reglages
  const loadModulePermissions = useCallback(async () => {
    try {
      const res = await api.get('/settings/modules/permissions');
      const perms = res.data.permissions || {};
      // Merge with defaults for system modules not in DB config
      const merged = { ...DEFAULT_PERMISSIONS, ...perms };
      setModulePermissions(merged);
    } catch {
      setModulePermissions(null);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('bouffi_token');
      const savedUser = localStorage.getItem('bouffi_user');
      
      if (token && savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          
          // Charger les permissions
          await loadPermissions();
          await loadModulePermissions();
          
          // Seed demo data on first load
          try {
            await seedAPI.seed();
          } catch (e) {
            // Already seeded, ignore
          }
          
          // Sélectionner automatiquement la première boutique pour licencié/manager
          if (userData.role === 'licencie' || userData.role === 'manager') {
            if (userData.boutiques_ids?.length > 0 && !localStorage.getItem('boufi_boutique_id')) {
              setSelectedBoutiqueId(userData.boutiques_ids[0]);
            }
          }
        } catch (error) {
          localStorage.removeItem('bouffi_token');
          localStorage.removeItem('bouffi_user');
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, [loadPermissions]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, user: userData } = response.data;
    
    localStorage.setItem('bouffi_token', token);
    localStorage.setItem('bouffi_user', JSON.stringify(userData));
    setUser(userData);
    
    // Charger les permissions
    await loadPermissions();
    
    // Seed demo data after login
    try {
      await seedAPI.seed();
    } catch (e) {
      // Already seeded, ignore
    }
    
    // Sélectionner automatiquement la première boutique pour licencié/manager
    if (userData.role === 'licencie' || userData.role === 'manager') {
      if (userData.boutiques_ids?.length > 0 && !localStorage.getItem('boufi_boutique_id')) {
        setSelectedBoutiqueId(userData.boutiques_ids[0]);
      }
    }
    
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { token, user: userData } = response.data;
    
    localStorage.setItem('bouffi_token', token);
    localStorage.setItem('bouffi_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('bouffi_token');
    localStorage.removeItem('bouffi_user');
    setUser(null);
    setPermissions({});
    setSelectedBoutiqueId(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    permissions,
    hasPermission,
    canAccessBoutique,
    hasAllBoutiques,
    selectedBoutiqueId,
    setSelectedBoutiqueId,
    // Helpers par rôle
    isAdmin: user?.role === 'admin',
    isInspecteur: user?.role === 'inspecteur',
    isLicencie: user?.role === 'licencie',
    isManager: user?.role === 'manager',
    isAssocie: user?.role === 'associe',
    isReadOnly: Boolean(user?.read_only),
    // Label du rôle
    roleLabel: ROLE_LABELS[user?.role] || user?.role,
    // Boutiques autorisées
    userBoutiquesIds: user?.boutiques_ids || [],
    // Reload module permissions (pour apres mise a jour dans Reglages)
    reloadModulePermissions: loadModulePermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
